import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { AnimatedText } from '$partials/ui/index.js';
import { AnimatedNumberCounter } from '$partials/modules/individual/animated-number-counter.jsx';

import { CarouselProvider } from '$contexts/carousel.jsx';
import Carousel, { CarouselSlide } from '$partials/ui/carousel.jsx';

import {
    IconSquiggle1,
    IconSquiggle2
} from '$partials/icons/index.js';

gsap.registerPlugin(ScrollTrigger);

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 */
const HomePageHero = (props) => {
    // references
    const containerRef = useRef();
    const timerRef = useRef();
    const splitRef = useRef();
    const timelineRef = useRef();

    // state
    const [ statsAnimationEnd, setStatsAnimationEnd ] = useState(false);
    const [ windowSize, setWindowSize ] = useState({ height: undefined, width: undefined });

    // methods
    const createAnimation = () => {
        if (containerRef.current) {
            // create timeline
            const tl = gsap.timeline({
                scrollTrigger: {
                    // markers: true,
                    start: 'top center',
                    trigger: containerRef.current
                }
            });

            timelineRef.current = tl;

            // create reveal hide of split text content
            tl.to(
                containerRef.current.querySelectorAll('.animation__mask-slide-in'),
                {
                    duration: 0.1,
                    opacity: 1,
                    overwrite: true
                },
                0
            );

            // create split text object
            const split = SplitText.create(
                containerRef.current.querySelectorAll('.animated-statement__lines'),
                {
                    aria: 'none',
                    linesClass: 'animation__line',
                    mask: 'lines',
                    onSplit: (self) => {
                        // on complete of split, create and add tween to timeline
                        const to = tl.to(
                            self.lines,
                            {
                                duration: 0.8,
                                ease: 'power3',
                                overwrite: true,
                                stagger: 0.15,
                                y: '0%'
                            },
                            0
                        );

                        return to;
                    },
                    type: 'lines'
                }
            );

            splitRef.current = split;

            // create scroll up tween and add to timeline
            tl.to(
                containerRef.current.querySelectorAll('.animation__scroll-up'),
                {
                    duration: 3,
                    ease: 'power3',
                    overwrite: true,
                    y: (i, target) => {
                        return (target.offsetHeight - (target.firstChild.offsetHeight)) * -1;
                    }
                },
                0
            );

            tl.to(
                containerRef.current.querySelectorAll('.animation__scale-blur-fade-in'),
                {
                    duration: 1,
                    ease: 'smooth',
                    filter: 'blur(0px)',
                    opacity: 1,
                    overwrite: true,
                    scale: 1
                },
                0
            );

            tl.to(
                containerRef.current.querySelectorAll('.animation__fade-in'),
                {
                    delay: 1,
                    duration: 1,
                    ease: 'power3',
                    opacity: 1,
                    overwrite: true
                },
                0
            );
        }
    };

    // hooks
    useEffect(() => {
        const handleResize = () => {
            // only recreate animation when resize width changes
            if (window.innerWidth !== windowSize.width) {
                // clear split & timeline
                splitRef?.current?.revert?.();
                timelineRef?.current?.revert?.();

                // debounce split/timeline creation
                timerRef.current && clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    setWindowSize({ height: window.innerHeight, width: window.innerWidth });
                    createAnimation();
                }, 100);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize, { passive: true });
        return () => window.removeEventListener('resize', handleResize);
    }, [ windowSize.width ]);

    // render
    return (
        <section
            className='relative overflow-hidden'
            ref={containerRef}>
            <div
                className='relative pb-178'>
                {/* image */}
                {props.headerBackgroundImage && (
                    <div
                        aria-hidden='true'
                        className='absolute inset-0 h-full w-full overflow-hidden'>
                        <img
                            alt=''
                            className='animation__scale-blur-fade-in absolute inset-0 h-full w-full object-center object-cover select-none pointer-events-none'
                            height={props.headerBackgroundImage.height}
                            loading='eager'
                            src={`${props?.headerBackgroundImage.src}?fm=webp&q=50&w=1440`}
                            width={props.headerBackgroundImage.width} />

                        <div className='animation__scale-blur-fade-in absolute inset-0 media-top-bottom-gradient' />
                    </div>
                )}

                {/* ribbon */}
                <div className='animation__fade-in absolute inset-0 pointer-events-none'>
                    <div
                        className='absolute'
                        style={{
                            left: '-133px',
                            top: '-169px'
                        }}><IconSquiggle1 /></div>

                    <div
                        className='absolute'
                        style={{
                            bottom: '-48.5px',
                            right: '-486.509px'
                        }}><IconSquiggle2 /></div>
                </div>

                {/* text */}
                <div className='relative h-svh min-h-600 flex items-center py-178'>
                    <div className='absolute top-1/2 -translate-y-1/4 w-full pb-20'>
                        <div className='animated-statement animated-statement--white'>
                            <div className='layout-container w-full overflow-hidden'>
                                <div className='w-full sm:w-10/12 px-10 sm:px-5 md:px-10'>
                                    {props?.animatedText && (
                                        <div className='type-style-display break-words animated-statement__text pr-38'>
                                            <AnimatedText richText={props.animatedText} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* stats */}
            <div className='relative -mt-115 md:-mt-140'>
                <div className='layout-container'>
                    {/* desktop */}
                    <ul className='hidden md:flex flex-wrap'>
                        {props?.statistics?.items?.map((item, idx) => {
                            return (
                                <li
                                    className='w-[235px] md:w-3/12 md:min-w-[270px] grow-0 shrink-0 py-30 px-10 sm:px-5 md:px-10'
                                    key={idx}>
                                    <div className='h-full flex flex-col gap-2 md:gap-8 px-20 py-16 md:p-40 rounded-button bg-core-sand'>
                                        <div
                                            className='type-style-6 text-grey-black'
                                            style={{ lineHeight: 1 }}>
                                            {statsAnimationEnd ? (
                                                <p>{item.statistic}</p>
                                            ) : (
                                                <AnimatedNumberCounter
                                                    onEnd={() => setStatsAnimationEnd(true)}
                                                    toValue={parseFloat(item.statistic)} />
                                            )}
                                        </div>

                                        <p className='type-style-8 text-grey-off-black mt-2 md:mt-8'>
                                            {item?.body}
                                        </p>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* tablet / mobile carousel */}
                <div className='md:hidden relative overflow-hidden'>
                    <CarouselProvider swiperConfig={{
                        breakpoints: {
                            768: {
                                slidesOffsetBefore: 35
                            }
                        },
                        slidesOffsetBefore: 10
                    }}>
                        <Carousel className='flex swiper-wrapper h-auto w-full'>
                            {props?.statistics?.items?.map((item, idx) => {
                                if (!item) {return null;}

                                return (
                                    <CarouselSlide
                                        className='w-[235px] md:w-3/12 md:min-w-[270px] grow-0 shrink-0 py-30 px-10 sm:px-5 md:px-10'
                                        key={idx}>
                                        <div className='h-full flex flex-col gap-2 md:gap-8 px-20 py-16 md:p-40 rounded-button bg-core-sand'>
                                            <div
                                                className='type-style-6 text-grey-black'
                                                style={{ lineHeight: 1 }}>
                                                {statsAnimationEnd ? (
                                                    <p>{item.statistic}</p>
                                                ) : (
                                                    <AnimatedNumberCounter
                                                        onEnd={() => setStatsAnimationEnd(true)}
                                                        toValue={parseFloat(item.statistic)} />
                                                )}
                                            </div>

                                            <p className='type-style-8 text-grey-off-black mt-2 md:mt-8'>
                                                {item?.body}
                                            </p>
                                        </div>
                                    </CarouselSlide>
                                );
                            }).filter(Boolean)}
                        </Carousel>
                    </CarouselProvider>
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    headerBackgroundImage: PropTypes.object,
    animatedText: PropTypes.string.isRequired,
    statistics: PropTypes.object
};

HomePageHero.propTypes = propTypes;

export default HomePageHero;
